import { useCallback } from 'react';
import AblyManager, {
  GroupManagementNotificationData,
  GroupMessageNotificationData,
  GroupManagementEvent,
  GroupMessageEvent
} from '@/utils/ably-im/ably-manager';
import { useGroupStore } from '@/stores/useGroupStore';
import ToastUtils from '@/utils/toast-utils';
import { useChatActions } from './useChatActions';
import { logPrint } from '@/utils/my-utils';

/**
 * 群组通知处理器 Hook
 * 负责处理群组创建、加入、移除等通知事件
 */
export function useGroupNotificationHandler() {
  const groupStore = useGroupStore();
  const {
    getConversations,
  } = useChatActions();

  /**
   * 浏览器通知，显示群组状态更新通知
   * 
   * @param title 通知标题
   * @param content 通知内容
   * @param groupId 群组ID
   */
  const showGroupStatusNotification = useCallback(async (title: string, content: string, groupId: string) => {
    try {
      // 使用桌面通知
      if ('Notification' in window && Notification.permission === 'granted') {
        const notification = new Notification(title, {
          body: content,
          icon: '/icon.png',
          badge: '/icon.png',
          tag: `group_${groupId}`, // 同一群组的通知会互相替换
          requireInteraction: false,
          silent: false
        });

        // 设置自动关闭
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      logPrint(`群组状态通知已显示: ${title} - ${content}`);
    } catch (error) {
      console.error('显示群组状态通知失败:', error);
      // 如果桌面通知失败，至少显示 Toast
      ToastUtils.showToast(content, 4000);
    }
  }, []);

  /**
   * 处理群组管理通知（不加密，静默处理）
   * @param data 管理通知数据
   */
  const handleGroupManagementNotification = useCallback(async (data: GroupManagementNotificationData) => {
    const { type, groupId, data: eventData } = data;
    logPrint(`处理群组管理通知: ${type}, 群组ID: ${groupId}`, eventData);

    switch (type) {
      case GroupManagementEvent.group_created:
        // 收到群组创建通知，开始监听该群组（创建者不显示通知）
        try {
          await AblyManager.getInstance().subscribeToGroupChannel(groupId);
          groupStore.getGroupList();
          getConversations();
          logPrint(`已开始监听群组: ${groupId}`);
        } catch (error) {
          console.error(`处理群组创建通知失败 (${groupId}):`, error);
        }
        break;

      case GroupManagementEvent.group_enter:
        // 收到群组加入通知，开始监听该群组
        try {
          await AblyManager.getInstance().subscribeToGroupChannel(groupId);
          const groupMap = await groupStore.getGroupList();
          const group = Array.from(groupMap.values())?.find((g) => g.id === groupId);
          getConversations();

          const groupName = group?.name || `群组 ${groupId}`;
          await showGroupStatusNotification(
            '加入群组',
            `您被邀请加入了群组 ${groupName}`,
            groupId
          );
          logPrint(`已开始监听群组: ${groupId}，并显示邀请通知`);
        } catch (error) {
          console.error(`处理群组加入通知失败 (${groupId}):`, error);
        }
        break;

      case GroupManagementEvent.group_rmove:
        // 收到群组移除通知，停止监听该群组
        try {
          await AblyManager.getInstance().unsubscribeFromGroupChannel(groupId);
          groupStore.removeGroupFromList(groupId);
          getConversations();

          await showGroupStatusNotification(
            '移出群组',
            '您已被移出群组',
            groupId
          );
          logPrint(`已停止监听群组: ${groupId}`);
        } catch (error) {
          console.error(`处理群组移除通知失败 (${groupId}):`, error);
        }
        break;

      case GroupManagementEvent.group_permissions_updated:
        // 权限更新（静默处理）
        logPrint('群组权限已更新（静默处理）：', groupId, eventData);
        // TODO: 更新本地权限状态
        break;

      default:
        logPrint(`未处理的管理通知类型: ${type}`);
        break;
    }
  }, [groupStore, getConversations, showGroupStatusNotification]);

  /**
   * 处理群组消息通知（加密，显示在消息记录和通知用户）
   * @param data 消息通知数据
   */
  const handleGroupMessageNotification = useCallback(async (data: GroupMessageNotificationData) => {
    const { messageInfo } = data;
    const { ably_name, messageType, conversation_id, decryptedContentData } = messageInfo;

    const type = ably_name as GroupMessageEvent;
    const groupStore = useGroupStore.getState()
    const groupId = groupStore.groupMap.get(conversation_id)?.id ?? conversation_id;
    let eventData = decryptedContentData;
    logPrint(`处理群组消息通知: ${messageType}, 群组ID: ${groupId}`, eventData);

    // 这些是权限相关的消息通知，静默处理
    const permissionMessageEvents = [
      GroupMessageEvent.group_mute_enabled,
      GroupMessageEvent.group_mute_disabled,
      GroupMessageEvent.group_screenshot_enabled,
      GroupMessageEvent.group_screenshot_disabled,
      GroupMessageEvent.group_send_card_enabled,
      GroupMessageEvent.group_send_card_disabled,
      GroupMessageEvent.group_send_image_enabled,
      GroupMessageEvent.group_send_image_disabled,
      GroupMessageEvent.group_send_video_enabled,
      GroupMessageEvent.group_send_video_disabled,
      GroupMessageEvent.group_send_file_enabled,
      GroupMessageEvent.group_send_file_disabled,
    ];

    const jsonData = JSON.parse(eventData?.content || '{}');
    if (eventData) {
      eventData.invite_name = jsonData.invite_name;
      eventData.member_names = jsonData.member_names;
      eventData.member_handles = jsonData.member_handles;
      eventData.group_update_type = jsonData.group_update_type;
      eventData.group_name = jsonData.group_name;
      eventData.group_avatar = jsonData.group_avatar;
      eventData.group_color = jsonData.group_color;
      eventData.permissions = jsonData.permissions;
      eventData.notice = jsonData.notice;
      eventData.message_id = jsonData.message_id;
      eventData.message_content = jsonData.message_content;
      eventData.sender_id = jsonData.sender_id;
      eventData.sender_name = jsonData.sender_name;
    }

    if (permissionMessageEvents.includes(type)) {
      logPrint(`群组权限消息通知（静默处理）: ${type}`, eventData);
      groupStore.updateGroupInList(groupId, { permissions: eventData?.permissions });
      return;
    }

    // 处理状态更新类消息通知（需要用户通知）
    switch (type) {
      case GroupMessageEvent.group_new_create: {
        // 群组创建消息（显示在聊天中）
        const senderName = eventData?.sender_name || '群主';
        await showGroupStatusNotification(
          '群组创建',
          `${senderName} 创建了群组`,
          groupId
        );
        logPrint(`群组创建消息: ${groupId}`);
        break;
      }

      case GroupMessageEvent.group_new_invite: {
        // 群组邀请消息
        const inviteName = eventData?.invite_name || '群主';
        const memberNames = eventData?.member_names || [];
        const invitedUsers = Array.isArray(memberNames) ? memberNames.join(', ') : '新成员';

        await showGroupStatusNotification(
          '群组邀请',
          `${inviteName} 邀请 ${invitedUsers} 加入了群组`,
          groupId
        );
        logPrint(`群组邀请消息: ${groupId}`);
        break;
      }

      case GroupMessageEvent.group_info_update: {
        // 群组信息更新消息
        const senderName = eventData?.sender_name || '群主';
        const updateType = eventData?.group_update_type;
        let updateMsg = '更新了群组信息';

        if (updateType === 'name' && eventData?.group_name) {
          updateMsg = `将群组名称修改为"${eventData.group_name}"`;
        } else if (updateType === 'avatar') {
          updateMsg = '更新了群组头像';
        } else if (updateType === 'color') {
          updateMsg = '更新了群组颜色';
        }

        await showGroupStatusNotification(
          '群组信息更新',
          `${senderName} ${updateMsg}`,
          groupId
        );
        logPrint(`群组信息更新消息: ${groupId}`);
        break;
      }

      case GroupMessageEvent.group_dissolve: {
        // 群组解散
        const senderName = eventData?.sender_name || '群主';
        await AblyManager.getInstance().unsubscribeFromGroupChannel(groupId);
        groupStore.removeGroupFromList(groupId);
        getConversations();

        await showGroupStatusNotification(
          '群组解散',
          `${senderName} 解散了群组`,
          groupId
        );
        logPrint(`群组已解散: ${groupId}`);
        break;
      }

      case GroupMessageEvent.group_member_added: {
        // 群组成员添加
        const inviteName = eventData?.invite_name || '群主';
        const memberNames = eventData?.member_names || [];
        const addedUsers = Array.isArray(memberNames) ? memberNames.join(', ') : '新成员';

        await showGroupStatusNotification(
          '新成员加入',
          `${inviteName} 添加了 ${addedUsers}`,
          groupId
        );
        logPrint(`群组成员添加: ${groupId}`);
        break;
      }

      case GroupMessageEvent.group_member_removed: {
        // 群组成员移除
        const senderName = eventData?.sender_name || '群主';
        const memberNames = eventData?.member_names || [];
        const removedUsers = Array.isArray(memberNames) ? memberNames.join(', ') : '成员';

        await showGroupStatusNotification(
          '成员被移除',
          `${senderName} 移除了 ${removedUsers}`,
          groupId
        );
        logPrint(`群组成员移除: ${groupId}`);
        break;
      }

      case GroupMessageEvent.group_member_leave: {
        // 群组成员离开
        const senderName = eventData?.sender_name || '未知用户';

        await showGroupStatusNotification(
          '成员离开',
          `${senderName} 离开了群组`,
          groupId
        );
        logPrint(`群组成员离开: ${groupId}`);
        break;
      }

      case GroupMessageEvent.group_notice_updated: {
        // 群组公告更新
        const senderName = eventData?.sender_name || '群主';
        const notice = eventData?.notice || '';
        const noticePreview = notice.length > 20 ? notice.substring(0, 20) + '...' : notice;

        await showGroupStatusNotification(
          '群组公告',
          `${senderName} 发布了新公告: ${noticePreview}`,
          groupId
        );
        logPrint(`群组公告更新: ${groupId}`);
        break;
      }

      case GroupMessageEvent.group_pinned_message_add: {
        // 群组置顶消息添加
        const senderName = eventData?.sender_name || '群主';

        await showGroupStatusNotification(
          '消息置顶',
          `${senderName} 置顶了一条消息`,
          groupId
        );
        logPrint(`群组消息置顶: ${groupId}`);
        break;
      }

      case GroupMessageEvent.group_pinned_message_delete: {
        // 群组置顶消息取消
        const senderName = eventData?.sender_name || '群主';

        await showGroupStatusNotification(
          '取消置顶',
          `${senderName} 取消了消息置顶`,
          groupId
        );
        logPrint(`群组消息取消置顶: ${groupId}`);
        break;
      }

      default:
        console.warn(`未知的群组消息通知类型: ${type}`);
        break;
    }
  }, [groupStore, getConversations, showGroupStatusNotification]);



  return {
    handleGroupManagementNotification,
    handleGroupMessageNotification,
    showGroupStatusNotification,
  };
}
